import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import {
  checkIsLoading,
  checkIsLoadingDone
} from 'common/utils/checkLoading';
import { turfBounds } from 'shared/utils';

import { RootState } from 'store';

import { NAVIGATION_STATE } from '../../constants';
import { EMPTY_ARRAY } from 'common/constants';
import { NavigationHistoryPath } from '../../types';

import {
  checkIsUntrackedLocationHistoryPath,
  checkIsParkingLocationHistoryPath
} from '../../utils';

export const useNavigationLocationsHistoryLoadingStatus = () => {
  const navigationLocationsHistoryLoadingStatus = useSelector((state: RootState) => state[NAVIGATION_STATE].history.loadingStatus);
  return navigationLocationsHistoryLoadingStatus;
};

export const useIsNavigationLocationsHistoryLoading = () => {
  const navigationLocationsHistoryLoadingStatus = useNavigationLocationsHistoryLoadingStatus();
  return checkIsLoading(navigationLocationsHistoryLoadingStatus);
};

export const useIsNavigationLocationsHistoryDone = () => {
  const navigationLocationsHistoryLoadingStatus = useNavigationLocationsHistoryLoadingStatus();
  return checkIsLoadingDone(navigationLocationsHistoryLoadingStatus);
};

export const useNavigationHistory = () => {
  const navigationHistory = useSelector((state: RootState) => state[NAVIGATION_STATE].history.data);
  return navigationHistory;
};

export const useNavigationHistoryPath = () => {
  const { path } = useNavigationHistory() || {};
  return path;
};

export const useNavigationHistoryBounds = () => {
  const navigationHistoryPath = useNavigationHistoryPath() || EMPTY_ARRAY;

  const navigationHistoryBounds = useMemo(() => {
    const coords = navigationHistoryPath.reduce((acc, { locations }) => {
      locations.forEach(({ latitude, longitude }) => {
        acc.push([ longitude, latitude ]);
      });
      return acc;
    }, [] as [number, number][]);

    return coords.length ? turfBounds(coords) : null
  }, [navigationHistoryPath]);

  return navigationHistoryBounds;
};

export const useNavigationUntrackedHistoryPath = () => {
  const navigationHistoryPath = useNavigationHistoryPath() || EMPTY_ARRAY;

  const navigationUntrackedHistoryPath = useMemo(() => navigationHistoryPath.reduce((acc, item) => {
    if (checkIsUntrackedLocationHistoryPath(item.type)) {
      acc.push(item);
    }
    return acc;
  }, [] as NavigationHistoryPath[]), [
    navigationHistoryPath
  ]);

  return navigationUntrackedHistoryPath;
};

export const useNavigationParkingHistoryPath = () => {
  const navigationHistoryPath = useNavigationHistoryPath() || EMPTY_ARRAY;

  const navigationParkingHistoryPath = useMemo(() => navigationHistoryPath.reduce((acc, item) => {
    if (checkIsParkingLocationHistoryPath(item.type)) {
      acc.push(item);
    }
    return acc;
  }, [] as NavigationHistoryPath[]), [
    navigationHistoryPath
  ]);

  return navigationParkingHistoryPath;
};

export const useNavigationAutoObuPairsLoadingStatus = () => {
  const navigationAutoObuPairsLoadingStatus = useSelector((state: RootState) => state[NAVIGATION_STATE].autos.loadingStatus);
  return navigationAutoObuPairsLoadingStatus;
};


export const useNavigationAutoObuPairs = () => {
  const navigationAutoObuPairs = useSelector((state: RootState) => state[NAVIGATION_STATE].autos.data);
  return navigationAutoObuPairs;
}

export const useHasNavigationAutoObuPairs = () => {
  const navigationAutoObuPairs = useNavigationAutoObuPairs();

  return !!(navigationAutoObuPairs && navigationAutoObuPairs.length);
};

export const useNavigationAutoObuPairList = () => {
  const navigationAutoObuPairs = useNavigationAutoObuPairs();

  const navigationAutoObuPairList = useMemo(() => (navigationAutoObuPairs || []).map(({
    autoNumber,
    obuNumber
  }) => ({
    key: autoNumber,
    obuNumber,
    value: `${autoNumber} (OBU ${obuNumber})`
  })), [
    navigationAutoObuPairs
  ]);

  return navigationAutoObuPairList;
};

